/// ///////////////////////////////////////////////////////////////////////////////
// Copyright (C) 2026 CONQUER INNOVATIONS SP. Z O.O. Global B.V. and its affiliate(s).
// All rights reserved.
//
// This software and other materials contain proprietary information
// controlled by CONQUER INNOVATIONS SP. Z O.O. and are protected by applicable copyright legislation.
// Any use and utilization of this software and other materials and
// disclosure to any third parties is conditional upon having a separate
// agreement with CONQUER INNOVATIONS SP. Z O.O. for the access, use, utilization or disclosure of this
// software. In the absence of such agreement, the use of the software is not
// allowed.
// The creator, author and originator of the code in the current file: Michał Pawłowski
//////////////////////////////////////////////////////////////////////////////////
<template>
  <div class="task-table-wrapper">
    <div v-if="loading" class="table-loading-box">
      <b-spinner small class="me-2"></b-spinner>
      Trwa pobieranie listy zadań...
    </div>

    <b-table
        v-else
        :items="items"
        :fields="fields"
        responsive="xl"
        striped
        hover
        small
        class="task-table mb-0"
        empty-text="Brak danych."
    >
      <template #empty>
        <div class="text-center text-muted py-4">
          {{ emptyText }}
        </div>
      </template>

      <template #cell(status)="data">
        <b-badge :variant="statusVariant(data.item.status)">
          {{ statusLabel(data.item.status) }}
        </b-badge>
      </template>

      <template #cell(message)="data">
        <div class="task-message-cell">
          {{ data.item.message || "-" }}
        </div>
      </template>

      <template #cell(startedAt)="data">
        {{ formatDateTime(data.item.startedAt || data.item.queuedAt) }}
      </template>

      <template #cell(finishedAt)="data">
        {{ formatDateTime(data.item.finishedAt) }}
      </template>

      <template #cell(progress)="data">
        <div class="task-progress-cell">
          <div class="task-progress-text">
            {{ formatProgress(data.item.progress) }}
          </div>

          <b-progress
              :value="normalizeProgressValue(data.item.progress)"
              :max="100"
              height="8px"
              class="task-progress-bar mt-1"
              :variant="progressVariant(data.item.status)"
          ></b-progress>
        </div>
      </template>

      <template #cell(actions)="data">
        <b-button
            size="sm"
            variant="outline-secondary"
            @click="openDetails(data.item)"
        >
          Szczegóły
        </b-button>
      </template>
    </b-table>
  </div>
</template>
<script>

export default {
  name: "TaskTable",
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    emptyText: {
      type: String,
      default: "Brak danych.",
    },
  },
  emits: ["show-details"],
  computed: {
    fields() {
      return [
        { key: "status", label: "Status", thStyle: { width: "140px" } },
        { key: "message", label: "Komunikat" },
        { key: "startedAt", label: "Start", thStyle: { width: "190px" } },
        { key: "finishedAt", label: "Koniec", thStyle: { width: "190px" } },
        { key: "progress", label: "Postęp", thStyle: { width: "110px" } },
        { key: "actions", label: "", thStyle: { width: "110px" } },
      ];
    },
  },
  methods: {
    normalizeProgressValue(value) {
      const parsed = Number(value);

      if (!Number.isFinite(parsed)) {
        return 0;
      }

      if (parsed <= 1) {
        return Math.max(0, Math.min(100, parsed * 100));
      }

      return Math.max(0, Math.min(100, parsed));
    },

    progressVariant(status) {
      if (status === "DONE") return "success";
      if (status === "FAILED") return "danger";
      if (status === "RUNNING") return "info";
      if (status === "QUEUED" || status === "NEW") return "secondary";
      return "primary";
    },
    statusLabel(status) {
      const labels = {
        NEW: "Nowe",
        QUEUED: "W kolejce",
        RUNNING: "W trakcie",
        DONE: "Zakończono",
        FAILED: "Błąd",
      };
      return labels[status] || status || "-";
    },
    statusVariant(status) {
      if (status === "DONE") return "success";
      if (status === "FAILED") return "danger";
      if (status === "RUNNING" || status === "QUEUED" || status === "NEW") return "primary";
      return "secondary";
    },
    formatDateTime(value) {
      if (!value) {
        return "-";
      }

      try {
        return new Date(value * 1000).toLocaleString();
      } catch (e) {
        return value;
      }
    },
    formatProgress(value) {
      const parsed = Number(value);
      if (!Number.isFinite(parsed)) {
        return "-";
      }

      if (parsed <= 1) {
        return `${Math.round(parsed * 100)}%`;
      }

      return `${Math.round(parsed)}%`;
    },
    openDetails(item) {
      this.$emit("show-details", item);
    },
  },

}
</script>
<style scoped>

.task-progress-cell {
  min-width: 110px;
}

.task-progress-text {
  font-size: 0.9rem;
  font-weight: 500;
  color: #334155;
}

.task-progress-bar {
  min-width: 90px;
}
.task-table {
  font-size: 0.94rem;
}

.task-message-cell {
  max-width: 100%;
  white-space: normal;
  word-break: break-word;
}

.table-loading-box {
  display: flex;
  align-items: center;
  padding: 16px;
  color: #5b6577;
}

.task-details-modal-body {
  background: #fafbfd;
}
</style>

